import { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FaUser,
  FaCertificate,
  FaClock,
  FaArrowLeft,
  FaSpinner,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaBell,
  FaBookOpen,
  FaUserTie,
  FaTag,
  FaCalendarAlt,
  FaCheckCircle,
  FaCreditCard,
  FaRupeeSign,
  FaExclamationTriangle,
  FaChalkboardTeacher,
  FaRunning,
  FaUtensils,
  FaDumbbell,
  FaMagic,
  FaSeedling,
  FaHeartbeat,
  FaEllipsisH,
  FaStar,
  FaBook,
  FaMedal,
  FaLanguage,
  FaSignInAlt,
  FaUserPlus,
  FaMapMarkerAlt,
  FaCity,
  FaHome,
  FaPhone,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import TrainerAvailabilityNotificationModal from '../Components/TrainerAvailabilityNotificationModal';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import Avatar from '../Components/Avatar';
import StarRating from '../Components/StarRating';
import { formatName, toTitleCase } from '../utils/dataTransformers';
import { formatCurrency } from '../utils/currencyFormatter';
import UserContext from '../Components/UserContext';
import { useSWRConfig } from 'swr';
import AiEnrollmentModal from '../Components/AiEnrollmentModal';
import { createPortal } from 'react-dom';
import { AIConsultantsData } from '../constants/aiAgents';
// Map URL-friendly service names to actual service names
const serviceNameMap = {
  'yoga-trainer': 'Yoga Trainer',
  'zumba-trainer': 'Zumba Trainer',
  'dietitian': 'Dietitian',
  'astrologer': 'Astrologer',
  'reiki-healer': 'Reiki Healer',
  'home-tuition': 'Home Tuition',
  'ayurvedacharya': 'Ayurveda Practitioner',
  'numerologer': 'Numerologer',
  'tarot-card-reader': 'Tarot Card Reader',
  'gym-trainer': 'Gym Trainer',
  'home-tutor': 'Home Tutor'
};

function Services() {
  const { serviceName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, setSession } = useContext(UserContext);
  const { mutate } = useSWRConfig();
  const currentUser = session || { id: '', name: '', email: '', role: 'user' };
  const isStudentRoute = location.pathname.startsWith('/student/');
  // Fallback to checking token directly for public routes where Guard isn't mounted
  const [hasToken, setHasToken] = useState(() => {
    try {
      return document.cookie.split(';').some(c => c.trim().startsWith('token='));
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    const checkToken = document.cookie.split(';').some(c => c.trim().startsWith('token='));
    if (checkToken !== hasToken) {
      setHasToken(checkToken);
    }
  }, [hasToken]);

  const isAuthenticated = !!session || hasToken;

  const [trainers, setTrainers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('rating'); // 'rating', 'name', 'experience', 'certification'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Number of trainers per page
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // Profile Completion States
  const [showQuickProfileModal, setShowQuickProfileModal] = useState(false);
  const [quickProfileData, setQuickProfileData] = useState({
    mobile: '',
    gender: 'male',
    addressLine1: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  const isProfileComplete = (sess) => {
    return !!(
      sess?.mobile && 
      sess?.gender && 
      sess?.address?.addressLine1 && 
      sess?.address?.city && 
      sess?.address?.state && 
      sess?.address?.pincode
    );
  };

  // Sync quick profile data with session if session exists but incomplete
  useEffect(() => {
    if (session && !isProfileComplete(session)) {
      setQuickProfileData({
        mobile: session.mobile || '',
        gender: session.gender || 'male',
        addressLine1: session.address?.addressLine1 || '',
        city: session.address?.city || '',
        state: session.address?.state || '',
        pincode: session.address?.pincode || ''
      });
    }
  }, [session]);


  // New state for enrollment functionality
  const [availableServices, setAvailableServices] = useState([]); // For service category selection
  const [selectedService, setSelectedService] = useState(null); // Selected service category
  const [selectedTrainer, setSelectedTrainer] = useState(null); // Selected trainer for enrollment
  const [selectedPrograms, setSelectedPrograms] = useState([]); // Selected programs
  const [selectedPaymentPlans, setSelectedPaymentPlans] = useState({}); // { programId: planId }
  const [viewingProfile, setViewingProfile] = useState(null); // Trainer profile modal
  const [referralCode, setReferralCode] = useState(''); // Referral code from URL
  const [isPWA, setIsPWA] = useState(false); // PWA mode detection
  const [apiError, setApiError] = useState(null); // API errors
  const [isAiAgentsEnabled, setIsAiAgentsEnabled] = useState(true); // Global settings
  const [disabledAiAgents, setDisabledAiAgents] = useState([]); // Array of disabled AI agent IDs

  // New state for collapsible categories in PWA
  const [isInstructorsExpanded, setIsInstructorsExpanded] = useState(true);
  const [isConsultantsExpanded, setIsConsultantsExpanded] = useState(true);

  // Collapse by default in PWA mode for better visibility
  useEffect(() => {
    if (isPWA) {
      setIsInstructorsExpanded(false);
      setIsConsultantsExpanded(false);
    }
  }, [isPWA]);

  // Refs to prevent duplicate calls and error logs (React StrictMode causes double renders in dev)
  const abortControllerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const lastServiceNameRef = useRef(null);
  const errorShownRef = useRef(false);
  const currentRequestRef = useRef(null); // Track the current request to prevent aborting it
  const trainerIdProcessedRef = useRef(false);

  // Get the actual service name from the URL parameter
  const actualServiceName = serviceNameMap[serviceName] || serviceName;

  // Fetch global settings
  useEffect(() => {
    HttpInterceptor.get('/api/admin/public/settings')
      .then(res => {
        if (res.data?.data) {
          setIsAiAgentsEnabled(res.data.data.isAiAgentsEnabled);
          setDisabledAiAgents(res.data.data.disabledAiAgents || []);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const [enrolledAiServices, setEnrolledAiServices] = useState([]);

  useEffect(() => {
    if (isAuthenticated || isStudentRoute) {
      HttpInterceptor.get('/api/ai/enrollments', { showErrorToast: false })
        .then(res => {
          if (res.data?.success) {
            setEnrolledAiServices(res.data.data.map(e => e.serviceType));
          }
        })
        .catch(err => {
          if (import.meta.env.DEV) {
            console.error('Error fetching AI enrollments:', err);
          }
        });
    }
  }, [isAuthenticated, isStudentRoute]);

  // Fetch available programs from backend (for service category selection)
  const fetchPrograms = useCallback(async () => {
    try {
      setApiError(null);
      setIsLoading(true);

      // Try authenticated endpoint first if user is logged in
      if (isAuthenticated) {
        try {
          const response = await HttpInterceptor.get('/api/students/programs', {
            showErrorToast: false
          });
          if (response.status === 200) {
            // Handle both array and object responses
            let servicesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);

            // Merge with serviceNameMap to ensure all categories show up even with 0 trainers
            const allServicesMap = new Map();

            // 1. Add all from serviceNameMap
            Object.entries(serviceNameMap).forEach(([key, name]) => {
              allServicesMap.set(key, {
                name: `${name} Programs`,
                type: key,
                trainers: [],
                trainerCount: 0
              });
            });

            // 2. Merge backend services
            servicesData.forEach(service => {
              const trainerCount = service.trainerCount !== undefined && service.trainerCount !== null
                ? Number(service.trainerCount)
                : (service.trainers && Array.isArray(service.trainers) ? service.trainers.length : 0);

              if (allServicesMap.has(service.type)) {
                allServicesMap.set(service.type, {
                  ...allServicesMap.get(service.type),
                  ...service,
                  trainerCount
                });
              } else {
                allServicesMap.set(service.type, {
                  ...service,
                  trainerCount
                });
              }
            });

            // 3. Sort by serviceNameMap order
            const serviceOrder = Object.keys(serviceNameMap);
            const finalServicesData = Array.from(allServicesMap.values()).sort((a, b) => {
              const indexA = serviceOrder.indexOf(a.type);
              const indexB = serviceOrder.indexOf(b.type);
              if (indexA !== -1 && indexB !== -1) return indexA - indexB;
              if (indexA !== -1) return -1;
              if (indexB !== -1) return 1;
              return (a.name || '').localeCompare(b.name || '');
            });

            setAvailableServices(finalServicesData);
            return;
          }
        } catch (authErr) {
          // If authenticated endpoint fails, fall through to public endpoint
          console.log('Authenticated endpoint failed, trying public endpoint');
        }
      }

      // For unauthenticated users or if authenticated endpoint fails, use trainers endpoint
      // to get available services (fetch all trainers to extract all unique services)
      try {
        const response = await HttpInterceptor.get('/api/trainers', {
          params: { limit: 1000 }, // Get enough trainers to extract all service types
          showErrorToast: false
        });

        if (response.status === 200) {
          // Handle different response structures
          let trainers = [];
          if (Array.isArray(response.data)) {
            trainers = response.data;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            trainers = response.data.data;
          } else if (response.data?.trainers && Array.isArray(response.data.trainers)) {
            trainers = response.data.trainers;
          }

          if (import.meta.env.DEV) {
            console.log('[Services] Fetched trainers for service extraction:', trainers.length);
          }

          // Extract unique services from trainers
          const serviceMap = new Map();
          trainers.forEach((trainer, trainerIndex) => {
            if (import.meta.env.DEV && trainerIndex === 0) {
              console.log('[Services] Sample trainer object:', {
                id: trainer.id,
                name: trainer.name,
                service: trainer.service,
                serviceType: typeof trainer.service,
                services: trainer.services,
                servicesType: Array.isArray(trainer.services) ? 'array' : typeof trainer.services
              });
            }

            // Check both service (single) and services (array) fields
            // Prefer services array if it has items, otherwise use service field
            let services = [];
            if (trainer.services && Array.isArray(trainer.services) && trainer.services.length > 0) {
              services = trainer.services;
            } else if (trainer.service !== undefined && trainer.service !== null) {
              // Handle both string and non-string service values
              services = [trainer.service];
            }

            if (import.meta.env.DEV && trainerIndex === 0) {
              console.log('[Services] Extracted services array:', services);
            }

            services.forEach(serviceName => {
              // Convert to string and trim - handle null, undefined, and non-string values
              const serviceStr = serviceName !== null && serviceName !== undefined
                ? String(serviceName).trim()
                : '';

              if (serviceStr) {
                if (!serviceMap.has(serviceStr)) {
                  // Create a service object similar to what /api/students/programs returns
                  const trainersForService = trainers.filter(t => {
                    let tServices = [];
                    if (t.services && Array.isArray(t.services) && t.services.length > 0) {
                      tServices = t.services;
                    } else if (t.service !== undefined && t.service !== null) {
                      tServices = [t.service];
                    }
                    return tServices.some(s => {
                      const sStr = s !== null && s !== undefined ? String(s).trim() : '';
                      return sStr === serviceStr;
                    });
                  });
                  serviceMap.set(serviceStr, {
                    name: `${serviceStr} Programs`,
                    type: serviceStr.toLowerCase().replace(/\s+/g, '-'),
                    trainers: trainersForService,
                    trainerCount: trainersForService.length
                  });

                  if (import.meta.env.DEV) {
                    console.log(`[Services] Created service "${serviceStr}" with ${trainersForService.length} trainers`);
                  }
                } else {
                  // If service already exists, update the trainers list
                  const existingService = serviceMap.get(serviceStr);
                  const trainersForService = trainers.filter(t => {
                    let tServices = [];
                    if (t.services && Array.isArray(t.services) && t.services.length > 0) {
                      tServices = t.services;
                    } else if (t.service !== undefined && t.service !== null) {
                      tServices = [t.service];
                    }
                    return tServices.some(s => {
                      const sStr = s !== null && s !== undefined ? String(s).trim() : '';
                      return sStr === serviceStr;
                    });
                  });
                  serviceMap.set(serviceStr, {
                    ...existingService,
                    trainers: trainersForService,
                    trainerCount: trainersForService.length
                  });

                  if (import.meta.env.DEV) {
                    console.log(`[Services] Updated service "${serviceStr}" with ${trainersForService.length} trainers`);
                  }
                }
              } else if (import.meta.env.DEV && trainerIndex === 0) {
                console.log('[Services] Skipped empty service name:', serviceName);
              }
            });
          });

          if (import.meta.env.DEV) {
            console.log('[Services] Extracted services from trainers:', serviceMap.size, Array.from(serviceMap.keys()));
          }

          // Merge services from trainers with serviceNameMap to ensure all categories are shown
          // Create a map of all possible services from serviceNameMap
          const allServicesMap = new Map();

          // First, add all services from serviceNameMap
          Object.entries(serviceNameMap).forEach(([key, name]) => {
            allServicesMap.set(key, {
              name: `${name} Programs`,
              type: key,
              trainers: [],
              trainerCount: 0
            });
          });

          // Then, merge in services found from trainers
          serviceMap.forEach((serviceData, serviceName) => {
            const normalizedServiceName = serviceName.toLowerCase().trim();

            // Try to find matching service in serviceNameMap by comparing names
            const matchingEntry = Object.entries(serviceNameMap).find(([key, name]) => {
              const normalizedMapName = name.toLowerCase().trim();
              const normalizedType = key.toLowerCase().trim();
              const normalizedServiceType = serviceData.type.toLowerCase().trim();

              // Match by exact name, or by type, or by converting names to types
              const serviceNameAsType = normalizedServiceName.replace(/\s+/g, '-');
              const mapNameAsType = normalizedMapName.replace(/\s+/g, '-');

              const matches = normalizedMapName === normalizedServiceName ||
                normalizedType === normalizedServiceType ||
                normalizedType === serviceNameAsType ||
                normalizedServiceType === mapNameAsType ||
                normalizedMapName.replace(/\s+/g, '-') === normalizedServiceType ||
                normalizedServiceName.replace(/\s+/g, '-') === normalizedType;

              if (import.meta.env.DEV && matches) {
                console.log(`[Services] Matched service "${serviceName}" with serviceNameMap key "${key}" (name: "${name}")`);
              }

              return matches;
            });

            if (matchingEntry) {
              // Update existing service with trainer data
              const [key, name] = matchingEntry;
              const existingService = allServicesMap.get(key);
              const trainerCount = serviceData.trainers && Array.isArray(serviceData.trainers)
                ? serviceData.trainers.length
                : (serviceData.trainerCount || 0);

              allServicesMap.set(key, {
                name: `${name} Programs`,
                type: key,
                trainers: serviceData.trainers || existingService?.trainers || [],
                trainerCount: trainerCount
              });

              if (import.meta.env.DEV) {
                console.log(`[Services] Updated service "${name}" with ${trainerCount} trainers`);
              }
            } else {
              // Add new service that's not in serviceNameMap (use the type as key)
              const trainerCount = serviceData.trainers && Array.isArray(serviceData.trainers)
                ? serviceData.trainers.length
                : (serviceData.trainerCount || 0);

              allServicesMap.set(serviceData.type, {
                ...serviceData,
                trainerCount: trainerCount
              });

              if (import.meta.env.DEV) {
                console.log(`[Services] Added new service "${serviceName}" with ${trainerCount} trainers (not in serviceNameMap)`);
              }
            }
          });

          // Convert to array and sort by serviceNameMap order (not alphabetically)
          const serviceOrder = Object.keys(serviceNameMap); // Maintain order from serviceNameMap
          const servicesData = Array.from(allServicesMap.values()).sort((a, b) => {
            const indexA = serviceOrder.indexOf(a.type);
            const indexB = serviceOrder.indexOf(b.type);
            // If both are in serviceNameMap, sort by their order
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only one is in serviceNameMap, prioritize it
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            // If neither is in serviceNameMap, sort alphabetically
            return a.name.localeCompare(b.name);
          });

          setAvailableServices(servicesData);
          return;
        }
      } catch (trainerErr) {
        console.log('Failed to fetch from trainers endpoint, using fallback');
      }

      // Fallback: Use serviceNameMap to create a list of all possible services
      // This ensures users always see all available service categories
      const fallbackServices = Object.entries(serviceNameMap).map(([key, name]) => ({
        name: `${name} Programs`,
        type: key,
        trainers: [],
        trainerCount: 0
      }));

      if (fallbackServices.length > 0) {
        setAvailableServices(fallbackServices);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load programs';
      // Only show error if it's not a 401 (expected for unauthenticated users)
      if (err.response?.status !== 401) {
        setApiError({ message: errorMessage });
        toast.error('Failed to load programs');
      } else {
        // For 401, silently fail and show empty state
        setApiError({ message: 'Please log in to view programs' });
      }
      setAvailableServices([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Handler function for program selection
  const handleProgramSelect = useCallback((program) => {
    // Don't allow selection of already enrolled programs
    if (program.isEnrolled) {
      toast.info('You are already enrolled in this program. Please check your enrolled programs.');
      return;
    }

    setSelectedPrograms(prev => {
      const isSelected = prev.some(p => p.id === program.id);
      if (isSelected) {
        // Remove program and its selected payment plan
        const newPlans = { ...selectedPaymentPlans };
        delete newPlans[program.id];
        setSelectedPaymentPlans(newPlans);
        return prev.filter(p => p.id !== program.id);
      } else {
        // Add program - if multiple plans exist, default to first one
        if (program.installmentInfo?.plans?.length > 1) {
          // Multiple plans - student should choose, but default to first
          setSelectedPaymentPlans(prev => ({
            ...prev,
            [program.id]: program.installmentInfo.plans[0].id
          }));
        } else if (program.installmentInfo?.plans?.length === 1) {
          // Single plan - auto-select it
          setSelectedPaymentPlans(prev => ({
            ...prev,
            [program.id]: program.installmentInfo.plans[0].id
          }));
        }
        return [...prev, program];
      }
    });
  }, [selectedPaymentPlans]);

  const handlePaymentPlanSelect = (programId, planId) => {
    setSelectedPaymentPlans(prev => ({
      ...prev,
      [programId]: planId
    }));
  };

  const addToTrainerStudents = async (trainerId, studentId, selectedPrograms) => {
    try {
      // Enroll in each selected program with individual error handling
      const enrollmentResults = await Promise.allSettled(
        selectedPrograms.map(program =>
          HttpInterceptor.post('/api/students/enroll', {
            programId: program.id,
            trainerId: trainerId,
            paymentPlanId: selectedPaymentPlans[program.id] || null, // Include selected plan or null
            referralCode: referralCode || undefined // Include referral code if provided
          })
        )
      );

      const successful = [];
      const failed = [];

      enrollmentResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.status === 201) {
          successful.push({
            program: selectedPrograms[index],
            data: result.value.data
          });
        } else {
          const error = result.status === 'rejected' ? result.reason : null;
          const errorMessage = error?.response?.data?.message || 'Enrollment failed';
          failed.push({
            program: selectedPrograms[index],
            error: errorMessage
          });
        }
      });

      // Show appropriate messages
      if (successful.length > 0 && failed.length === 0) {
        toast.success(`Successfully enrolled in ${successful.length} program${successful.length > 1 ? 's' : ''}`);
        return successful.map(s => s.data);
      } else if (successful.length > 0 && failed.length > 0) {
        // Partial success
        toast.warning(
          `Enrolled in ${successful.length} program${successful.length > 1 ? 's' : ''}, ` +
          `${failed.length} program${failed.length > 1 ? 's' : ''} ${failed.length > 1 ? 'were' : 'was'} already enrolled or failed`
        );
        return successful.map(s => s.data);
      } else {
        // All failed - provide user-friendly message
        const allAlreadyEnrolled = failed.every(f => f.error.includes('Already enrolled'));
        const errorMessages = failed.map(f => f.error).join(', ');

        if (allAlreadyEnrolled) {
          toast.info(`You are already enrolled in ${failed.length} program${failed.length > 1 ? 's' : ''}. Please check your enrolled programs.`);
        } else {
          toast.error(errorMessages || 'All enrollments failed. Please try again.');
        }

        // Throw error to prevent navigation
        const error = new Error(errorMessages || 'All enrollments failed');
        error.alreadyHandled = true; // Flag to prevent duplicate toast
        throw error;
      }
    } catch (error) {
      console.error('Error enrolling in programs:', error);
      // Only show error toast if not already shown above
      if (!error.alreadyHandled) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to enroll in programs';
        toast.error(errorMessage);
      }
      throw error;
    }
  };

  const handleSubmitPlanRequest = async () => {
    if (!selectedService && !actualServiceName) {
      setError('Please select a service');
      return;
    }
    if (!selectedTrainer && !viewingProfile) {
      setError('Please select a trainer');
      return;
    }
    if (selectedPrograms.length === 0) {
      setError('Please select at least one program');
      return;
    }

    if (isAuthenticated && !isProfileComplete(session)) {
      setShowQuickProfileModal(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      const trainer = selectedTrainer || viewingProfile;
      const enrollmentResults = await addToTrainerStudents(
        trainer.id,
        currentUser.id,
        selectedPrograms
      );

      // Create new plans based on the actual enrollment results
      const newPlans = enrollmentResults.map((enrollment, index) => ({
        id: enrollment.student._id || `plan_${Date.now()}_${index}`,
        name: selectedPrograms[index].name,
        price: selectedPrograms[index].price,
        trainer: trainer.name,
        trainerId: trainer.id,
        studentId: currentUser.id,
        program: selectedPrograms[index].description,
        status: enrollment.student.enrollment.status,
        paymentStatus: enrollment.student.payment.status,
        programDetails: {
          duration: selectedPrograms[index].duration,
          sessions: selectedPrograms[index].sessions,
          benefits: selectedPrograms[index].benefits
        }
      }));

      // Use actual number of successful enrollments for the message
      const enrolledCount = enrollmentResults.length;
      navigate('/student/plans', {
        state: {
          newPlans,
          message: `Your ${enrolledCount} program enrollment ${enrolledCount > 1 ? 'requests' : 'request'} ${enrolledCount > 1 ? 'have' : 'has'} been submitted!`
        }
      });
    } catch (err) {
      setError('Failed to enroll. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch programs when no serviceName is provided (for service category selection)
  useEffect(() => {
    if (!serviceName && !actualServiceName) {
      fetchPrograms();
    }
  }, [serviceName, actualServiceName, fetchPrograms]);

  // Initialize from URL params - with sanitization
  useEffect(() => {
    if (location.search.includes('service=') && availableServices.length > 0) {
      const serviceParam = new URLSearchParams(location.search).get('service');
      // Accept service names like "Yoga Trainer", "Dietitian", etc.
      if (serviceParam && typeof serviceParam === 'string') {
        // Sanitize: allow alphanumeric, spaces, hyphens, and underscores
        const sanitizedService = serviceParam.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim();
        if (sanitizedService && sanitizedService.length <= 50) {
          // First try to find by service type (for backward compatibility)
          let service = availableServices.find(s => s.type === sanitizedService);

          // If not found, try to find by matching trainers' service field
          if (!service && availableServices.length > 0) {
            // Use the first available service and filter trainers by service name
            service = { ...availableServices[0] };
            // Filter trainers by service name
            if (service.trainers) {
              service.trainers = service.trainers.filter(trainer =>
                trainer.service && trainer.service.toLowerCase() === sanitizedService.toLowerCase()
              );
            }
            // Only set if we found matching trainers
            if (service.trainers && service.trainers.length > 0) {
              setSelectedService(service);
            }
          } else if (service) {
            setSelectedService(service);
          }
        }
      }
    }
  }, [location.search, availableServices]);

  // Check for referral code in URL
  useEffect(() => {
    const refParam = new URLSearchParams(location.search).get('ref');
    if (refParam) {
      setReferralCode(refParam.toUpperCase().trim());
    }
  }, [location.search]);

  // Auto-select trainer from URL param (trainerId)
  useEffect(() => {
    const trainerIdParam = new URLSearchParams(location.search).get('trainerId');

    // Reset processed flag when trainerId changes
    if (trainerIdParam) {
      trainerIdProcessedRef.current = false;
    }

    if (trainerIdParam && availableServices.length > 0 && !selectedTrainer && !trainerIdProcessedRef.current) {
      // Sanitize trainerId: allow alphanumeric only
      if (typeof trainerIdParam === 'string') {
        const sanitizedTrainerId = trainerIdParam.replace(/[^a-zA-Z0-9]/g, '');
        if (sanitizedTrainerId) {
          // Search across all services to find the trainer
          let foundTrainer = null;
          let foundService = null;

          for (const service of availableServices) {
            if (service.trainers && Array.isArray(service.trainers)) {
              foundTrainer = service.trainers.find(trainer => {
                // Match by id (could be string or number)
                const trainerIdStr = String(trainer.id || trainer._id || '');
                return trainerIdStr === sanitizedTrainerId || trainerIdStr.replace(/[^a-zA-Z0-9]/g, '') === sanitizedTrainerId;
              });

              if (foundTrainer) {
                foundService = service;
                break;
              }
            }
          }

          // If trainer found, auto-select service and trainer
          if (foundTrainer && foundService) {
            trainerIdProcessedRef.current = true;
            setSelectedService(foundService);

            // Check for programIds in URL (can be multiple)
            const programIdParams = new URLSearchParams(location.search).getAll('programId');
            const sanitizedProgramIds = programIdParams
              .map(id => id.replace(/[^a-zA-Z0-9]/g, ''))
              .filter(id => id.length > 0);

            // Use setTimeout to ensure service is set before selecting trainer
            setTimeout(() => {
              setSelectedTrainer(foundTrainer);
              setViewingProfile(foundTrainer); // Set viewingProfile to show the instructor profile view
              setSelectedPrograms([]);
              setError(null);

              // If programIds are provided, try to auto-select the programs
              if (sanitizedProgramIds.length > 0 && foundTrainer.programs) {
                sanitizedProgramIds.forEach((sanitizedProgramId) => {
                  const foundProgram = foundTrainer.programs.find((p) => {
                    const programIdStr = String(p.id || p._id || '');
                    return programIdStr === sanitizedProgramId || programIdStr.replace(/[^a-zA-Z0-9]/g, '') === sanitizedProgramId;
                  });

                  if (foundProgram && !foundProgram.isEnrolled) {
                    // Auto-select the program
                    setTimeout(() => {
                      handleProgramSelect(foundProgram);
                    }, 200);
                  }
                });
              }
            }, 100);
          }
        }
      }
    }
  }, [location.search, availableServices, selectedTrainer, handleProgramSelect]);

  useEffect(() => {
    // Reset fetch flag if service name changed
    if (lastServiceNameRef.current !== actualServiceName) {
      lastServiceNameRef.current = actualServiceName;
      hasFetchedRef.current = false;
      errorShownRef.current = false;
      // Abort any previous request for a different service
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }

    // Prevent duplicate fetches for the same service (React StrictMode double render protection)
    if (hasFetchedRef.current && lastServiceNameRef.current === actualServiceName) {
      return;
    }

    if (!actualServiceName) {
      return;
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = `${actualServiceName}-${Date.now()}`;
    currentRequestRef.current = requestId;

    const fetchTrainers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        errorShownRef.current = false;

        const response = await HttpInterceptor.get('/api/trainers', {
          params: {
            service: actualServiceName,
            limit: 100 // Get more trainers for this service
          },
          showErrorToast: false,
          signal: abortController.signal
        });

        // Check if request was aborted or if this is no longer the current request
        if (abortController.signal.aborted || currentRequestRef.current !== requestId) {
          return; // Don't update state if request was aborted or superseded
        }

        // Handle successful response
        if (response && response.status === 200) {
          // Axios response structure: response.data is the parsed JSON body
          // API returns: { message: "...", data: [...], pagination: {...} }
          const trainersData = response.data?.data || response.data || [];
          const trainersArray = Array.isArray(trainersData) ? trainersData : [];

          // Don't treat "no trainers" as an error - it's a valid empty state
          // Only set error for actual failures (network, server errors, etc.)
          setError(null);

          setTrainers(trainersArray);
          // Mark as fetched only after successful completion
          hasFetchedRef.current = true;
        } else {
          // Unexpected response status
          if (import.meta.env.DEV) {
            console.warn('[Services] Unexpected response status:', response?.status);
          }
          setError('Unexpected response from server. Please try again.');
          setTrainers([]);
          // Mark as fetched even on error to prevent infinite retries
          hasFetchedRef.current = true;
        }
      } catch (err) {
        // Ignore abort errors - don't treat them as real errors
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || abortController.signal.aborted || currentRequestRef.current !== requestId) {
          return; // Don't update state or show errors for aborted/superseded requests
        }

        // Only log and show error once per fetch attempt
        if (!errorShownRef.current) {
          errorShownRef.current = true;

          // Debug logging - show full error details
          if (import.meta.env.DEV) {
            console.error('[Services] Error fetching trainers:', {
              error: err,
              message: err.message,
              type: err.type,
              code: err.code,
              response: err.response,
              responseData: err.response?.data,
              responseStatus: err.response?.status,
              config: err.config
            });
          }

          // Determine error message
          let errorMessage = 'Failed to load trainers. Please try again later.';
          if (err.type === 'network' || err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
            errorMessage = 'Network error. Please check your connection and try again.';
          } else if (err.response?.status === 404) {
            errorMessage = 'No trainers found for this service.';
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          }

          setError(errorMessage);

          // Only show toast if HttpInterceptor didn't already show it
          // HttpInterceptor shows toasts for network errors when showErrorToast is not false
          // Since we set showErrorToast: false, we need to show it ourselves, but only once
          if (err.type === 'network' && err.config?.showErrorToast === false) {
            // HttpInterceptor won't show toast, so we show it
            toast.error(errorMessage);
          } else if (err.type !== 'network') {
            // For non-network errors, show toast
            toast.error(errorMessage);
          }
        }
        setTrainers([]);
        // Mark as fetched even on error to prevent infinite retries
        hasFetchedRef.current = true;
      } finally {
        // Only update loading state if request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchTrainers();

    // Cleanup: abort request only if service name changed or component unmounts
    return () => {
      // Only abort if this is still the current request (not superseded by a new one)
      if (abortControllerRef.current && currentRequestRef.current === requestId) {
        abortControllerRef.current.abort();
        currentRequestRef.current = null;
      }
    };
  }, [actualServiceName, serviceName]);

  // Filter and sort trainers
  const filteredAndSortedTrainers = useMemo(() => {
    // Use trainers from selectedService if available, otherwise use trainers from API
    let trainersToFilter = selectedService && selectedService.trainers ? [...selectedService.trainers] : [...trainers];

    if (isAiAgentsEnabled) {
      const activeAIConsultants = AIConsultantsData.filter(ai => !disabledAiAgents.includes(ai.id));
      
      if (selectedService || actualServiceName) {
        let tgtType = '';
        if (selectedService) {
          tgtType = selectedService.type;
        } else {
          const matchedKey = Object.entries(serviceNameMap).find(([k, v]) => v === actualServiceName || k === actualServiceName)?.[0];
          tgtType = matchedKey || actualServiceName?.toLowerCase().replace(/\s+/g, '-');
        }
        const searchName = (actualServiceName || '').toLowerCase();

        const aiMatch = activeAIConsultants.find(ai =>
          ai.type === tgtType ||
          ai.service.toLowerCase().replace(/\s+/g, '-') === (tgtType || '').toLowerCase().replace(/\s+/g, '-') ||
          ai.service.toLowerCase() === searchName ||
          (selectedService && selectedService.name && ai.service.toLowerCase() === selectedService.name.toLowerCase().replace(/\sprograms?/i, ''))
        );

        if (aiMatch && !trainersToFilter.find(t => t.id === aiMatch.id)) {
          trainersToFilter.push(aiMatch);
        }
      } else {
        const toAdd = activeAIConsultants.filter(ai => !trainersToFilter.find(t => t.id === ai.id));
        trainersToFilter.push(...toAdd);
      }
    }

    // First, filter by search term
    let filtered = trainersToFilter.filter(trainer => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        trainer.name?.toLowerCase().includes(searchLower) ||
        trainer.bio?.toLowerCase().includes(searchLower) ||
        trainer.service?.toLowerCase().includes(searchLower) ||
        trainer.certification?.toLowerCase().includes(searchLower) ||
        trainer.qualifications?.toLowerCase().includes(searchLower)
      );
    });

    // Then, sort the filtered results
    filtered.sort((a, b) => {
      // Always put AI at the end
      if (a.isAi && !b.isAi) return 1;
      if (!a.isAi && b.isAi) return -1;

      let aValue, bValue;

      switch (sortBy) {
        case 'rating':
          aValue = a.rating || 0;
          bValue = b.rating || 0;
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'experience':
          aValue = a.yearsOfExperience || 0;
          bValue = b.yearsOfExperience || 0;
          break;
        case 'certification':
          aValue = (a.certification || '').toLowerCase();
          bValue = (b.certification || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [trainers, selectedService, searchTerm, sortBy, sortOrder, isAiAgentsEnabled]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedTrainers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrainers = filteredAndSortedTrainers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order (except for name which defaults to ascending)
      setSortBy(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  // Get sort icon for a field
  const getSortIcon = (field) => {
    const isActive = sortBy === field;
    if (!isActive) {
      return <FaSort className="text-gray-400" />;
    }
    // When active, use white color to contrast with primary background
    return sortOrder === 'asc' ? <FaSortUp className="text-white" /> : <FaSortDown className="text-white" />;
  };

  const [showAiModal, setShowAiModal] = useState(false);
  const [selectedAiService, setSelectedAiService] = useState(null);

  // Handler for service selection
  const handleServiceSelect = (service) => {
    if (service.isAi) {
      if (isAuthenticated) {
        setSelectedAiService(service);
        setShowAiModal(true);
      } else {
        navigate(`/login?redirect=/student/services/${service.type || service.name.toLowerCase().replace(/\\s+/g, '-')}`);
      }
      return;
    }

    // Convert service name to URL-friendly format
    const serviceType = service.type || service.name.toLowerCase().replace(/\s+/g, '-');
    // Find the key in serviceNameMap that matches
    const serviceKey = Object.keys(serviceNameMap).find(key =>
      serviceNameMap[key] === service.name.replace(' Programs', '') ||
      serviceNameMap[key] === service.name.replace(' Programs', ' Trainer')
    ) || serviceType;

    if (isAuthenticated || isStudentRoute) {
      if (isStudentRoute) {
        navigate(`/student/services/${serviceKey}`);
      } else {
        setSelectedAiService(null);
        setSelectedService(service);
        navigate(`/student/services/${serviceKey}`);
      }
    } else {
      // For public users, navigate to services page
      navigate(`/services/${serviceKey}`);
    }
  };

  // Show program selection view when viewingProfile is set and user is authenticated
  if (viewingProfile && isAuthenticated) {
    return (
      <div className={`${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-white'} rounded-xl ${isPWA ? 'shadow-2xl border-2 border-teal-200/60' : 'shadow-sm'} p-6 sm:p-8 relative overflow-hidden`}>
        {isPWA && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
          </>
        )}

        {/* Back Button */}
        <button
          onClick={() => {
            setViewingProfile(null);
            setSelectedPrograms([]);
          }}
          className={`flex items-center ${isPWA ? 'text-teal-700 hover:text-teal-800 font-semibold mb-6 px-4 py-2 rounded-lg hover:bg-teal-50 transition-all relative z-10' : 'text-gray-600 hover:text-primary mb-6 transition-colors'}`}
        >
          <FaArrowLeft className={`mr-2 ${isPWA ? 'text-teal-600' : ''}`} />
          Back
        </button>

        {/* Trainer Profile Card */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-2xl' : 'bg-white border border-gray-200'} rounded-xl sm:rounded-2xl overflow-hidden relative mb-6`}>
          {isPWA && (
            <>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
            </>
          )}
          <div className={`${isPWA ? 'bg-gradient-to-br from-teal-500 via-blue-500 to-indigo-500' : 'bg-gradient-to-r from-primary to-dark'} p-6 sm:p-8 text-white relative z-10`}>
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="relative">
                {isPWA && (
                  <div className="absolute -inset-2 bg-white/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Avatar
                  src={viewingProfile.profileImage || viewingProfile.image}
                  name={viewingProfile.name}
                  size={isPWA ? 140 : 120}
                  className={isPWA ? 'border-4 border-white ring-4 ring-white/30 shadow-2xl' : 'border-4 border-white'}
                  alt={formatName(viewingProfile.name)}
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h1 className={`${isPWA ? 'text-3xl sm:text-4xl' : 'text-3xl'} font-bold mb-2 drop-shadow-lg`}>
                  {formatName(viewingProfile.name)}
                </h1>
                <div className="flex items-center justify-center md:justify-start mb-3">
                  <span className={`inline-block ${isPWA ? 'bg-white/30 backdrop-blur-sm px-4 py-1.5 rounded-xl text-sm font-semibold shadow-lg border border-white/20' : 'bg-white/20 px-3 py-1 rounded-full text-sm font-medium'}`}>
                    {toTitleCase(viewingProfile.service || viewingProfile.expertise || 'Trainer')}
                  </span>
                </div>
                <div className="flex items-center justify-center md:justify-start">
                  <StarRating
                    rating={viewingProfile.rating || 0}
                    size="lg"
                    showNumber={true}
                    showReviews={true}
                    reviews={viewingProfile.reviews || 0}
                    className="text-yellow-300"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`${isPWA ? 'p-6 sm:p-8' : 'p-8'} relative z-10`}>
            {/* Key Information */}
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-teal-50/50 to-blue-50/30 rounded-xl p-4 sm:p-5 border border-teal-200/50' : ''}`}>
              {(viewingProfile.yearsOfExperience > 0 || viewingProfile.experience) && (
                <div className={`flex items-center ${isPWA ? 'bg-white rounded-lg p-4 shadow-md border border-teal-100/50' : 'bg-gray-50 rounded-lg p-4 border border-gray-200'}`}>
                  <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl mr-4 shadow-sm ring-2 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-lg mr-3'}`}>
                    <FaClock className={`${isPWA ? 'text-teal-600' : 'text-primary'} text-xl`} />
                  </div>
                  <div>
                    <p className={`${isPWA ? 'text-xs text-teal-600 font-medium mb-1' : 'text-sm text-gray-500'}`}>Experience</p>
                    <p className={`${isPWA ? 'text-lg font-bold text-teal-900' : 'font-semibold text-gray-900'}`}>
                      {viewingProfile.experience || `${viewingProfile.yearsOfExperience || 0} years`}
                    </p>
                  </div>
                </div>
              )}
              {(viewingProfile.certification || viewingProfile.qualifications) && (
                <div className={`flex items-center ${isPWA ? 'bg-white rounded-lg p-4 shadow-md border border-teal-100/50' : 'bg-gray-50 rounded-lg p-4 border border-gray-200'}`}>
                  <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl mr-4 shadow-sm ring-2 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-lg mr-3'}`}>
                    <FaCertificate className={`${isPWA ? 'text-teal-600' : 'text-primary'} text-xl`} />
                  </div>
                  <div>
                    <p className={`${isPWA ? 'text-xs text-teal-600 font-medium mb-1' : 'text-sm text-gray-500'}`}>Certification</p>
                    <p className={`${isPWA ? 'text-lg font-bold text-teal-900' : 'font-semibold text-gray-900'}`}>
                      {viewingProfile.certification || viewingProfile.qualifications || 'Not specified'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bio */}
            {viewingProfile.bio && (
              <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 rounded-xl p-5 sm:p-6 border border-teal-200/50 shadow-md' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                    </div>
                  )}
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-900' : 'text-xl font-bold text-gray-900'}`}>About</h2>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                    </div>
                  )}
                </div>
                <p className={`${isPWA ? 'text-teal-800 leading-relaxed text-base sm:text-lg' : 'text-gray-700 leading-relaxed'}`}>{viewingProfile.bio}</p>
              </div>
            )}

            {/* Teaching Style */}
            {viewingProfile.teachingStyle && (
              <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-5 sm:p-6 border border-blue-200/50 shadow-md' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-blue-400/40 via-indigo-400/40 to-purple-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 rounded-full"></div>
                    </div>
                  )}
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-blue-900' : 'text-xl font-bold text-gray-900'}`}>
                    {viewingProfile.isConsultant || viewingProfile.partnerType === 'consultant' ? 'Consultation Approach' : 'Teaching Style'}
                  </h2>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-blue-400/40 via-indigo-400/40 to-purple-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500 rounded-full"></div>
                    </div>
                  )}
                </div>
                <p className={`${isPWA ? 'text-blue-800 text-base sm:text-lg' : 'text-gray-700'}`}>{viewingProfile.teachingStyle}</p>
              </div>
            )}

            {/* Languages */}
            {viewingProfile.languages && viewingProfile.languages.length > 0 && (
              <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-indigo-50/30 rounded-xl p-5 sm:p-6 border border-indigo-200/50 shadow-md' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-indigo-400/40 via-purple-400/40 to-pink-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
                    </div>
                  )}
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-indigo-900' : 'text-xl font-bold text-gray-900'}`}>Languages</h2>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-indigo-400/40 via-purple-400/40 to-pink-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {viewingProfile.languages.map((lang, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center ${isPWA ? 'px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 border border-indigo-200/50 text-sm font-semibold shadow-sm' : 'px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium'}`}
                    >
                      <FaLanguage className={`mr-1.5 ${isPWA ? 'text-indigo-600' : ''}`} />
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements */}
            {viewingProfile.achievements && viewingProfile.achievements.length > 0 && (
              <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-yellow-50/30 rounded-xl p-5 sm:p-6 border border-yellow-200/50 shadow-md' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-yellow-400/40 via-orange-400/40 to-amber-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-yellow-500 via-orange-500 to-amber-500 rounded-full"></div>
                    </div>
                  )}
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-yellow-900' : 'text-xl font-bold text-gray-900'}`}>Achievements</h2>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-yellow-400/40 via-orange-400/40 to-amber-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-yellow-500 via-orange-500 to-amber-500 rounded-full"></div>
                    </div>
                  )}
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {viewingProfile.achievements.map((achievement, index) => (
                    <li key={index} className={`flex items-start ${isPWA ? 'bg-white rounded-lg p-3 shadow-sm border border-yellow-100/50' : 'text-gray-700'}`}>
                      <div className={`${isPWA ? 'bg-gradient-to-br from-yellow-100 to-orange-100 p-2 rounded-lg mr-3 shadow-sm ring-1 ring-yellow-200/50 flex-shrink-0' : ''}`}>
                        <FaMedal className={`${isPWA ? 'text-yellow-600' : 'text-yellow-500'} ${isPWA ? '' : 'mr-2'} mt-1 flex-shrink-0`} />
                      </div>
                      <span className={isPWA ? 'text-yellow-900 font-medium text-base' : ''}>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Programs */}
            {viewingProfile.programs && viewingProfile.programs.length > 0 && (
              <div className={`mb-6 sm:mb-8 ${isPWA ? 'bg-gradient-to-br from-white to-cyan-50/30 rounded-xl p-5 sm:p-6 border border-cyan-200/50 shadow-md' : ''}`}>
                <div className="flex items-center gap-2 mb-4">
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-cyan-400/40 via-teal-400/40 to-blue-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-cyan-500 via-teal-500 to-blue-500 rounded-full"></div>
                    </div>
                  )}
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-cyan-900' : 'text-xl font-bold text-gray-900'}`}>
                    {viewingProfile.isConsultant || viewingProfile.partnerType === 'consultant' ? 'Available Consultation Programs' : 'Available Programs'}
                  </h2>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1 h-5 bg-gradient-to-b from-cyan-400/40 via-teal-400/40 to-blue-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1 h-5 bg-gradient-to-b from-cyan-500 via-teal-500 to-blue-500 rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className={`grid grid-cols-1 ${isPWA ? 'sm:grid-cols-2' : 'md:grid-cols-2'} gap-4 sm:gap-5`}>
                  {viewingProfile.programs.map((program) => {
                    const isConsultantProfile = viewingProfile.isConsultant || viewingProfile.partnerType === 'consultant';
                    const isConsultantProgram = isConsultantProfile || program.partnerType === 'consultant' || program.timeWindowStart || program.slotDurations || Array.isArray(program.slotDurations);

                    let durationText = '';
                    let classesText = '';
                    let timeWindowText = '';
                    let slotDurationsText = '';

                    if (isConsultantProgram) {
                      if (program.timeWindowStart && program.timeWindowEnd) {
                        timeWindowText = `${program.timeWindowStart} - ${program.timeWindowEnd}`;
                      } else {
                        timeWindowText = 'Available by appointment';
                      }

                      if (program.slotDurations) {
                        const slots = Array.isArray(program.slotDurations)
                          ? program.slotDurations
                          : Object.keys(program.slotDurations || {});
                        if (slots.length > 0) {
                          slotDurationsText = slots.join(', ');
                        }
                      }
                    } else {
                      durationText = program.duration
                        ? (program.durationUnit
                          ? `${program.duration} ${program.durationUnit}`
                          : (typeof program.duration === 'string' ? program.duration : `${program.duration} weeks`))
                        : '';

                      // Calculate sessions per week from classesPerDuration and duration
                      // This ensures consistency with Services.jsx display
                      if (program.classesPerDuration && program.duration && program.durationUnit) {
                        // Calculate sessions per week
                        let totalWeeks = 0;
                        if (program.durationUnit === 'weeks') {
                          totalWeeks = program.duration;
                        } else if (program.durationUnit === 'months') {
                          totalWeeks = program.duration * 4.33; // Average weeks per month
                        } else if (program.durationUnit === 'years') {
                          totalWeeks = program.duration * 52; // Weeks per year
                        }

                        if (totalWeeks > 0) {
                          const sessionsPerWeek = Math.round((program.classesPerDuration / totalWeeks) * 10) / 10; // Round to 1 decimal
                          classesText = `${sessionsPerWeek} sessions/week`;
                        } else {
                          // Fallback: show total classes if calculation fails
                          classesText = `${program.classesPerDuration} classes`;
                        }
                      } else if (program.sessions) {
                        // Parse from sessions field if available (from student.controller)
                        const sessionsStr = String(program.sessions);
                        const frequencyMatch = sessionsStr.match(/\((\d+)x\/week\)/);
                        if (frequencyMatch) {
                          classesText = `${frequencyMatch[1]} sessions/week`;
                        } else {
                          // Try to extract just the number before "sessions"
                          const sessionsMatch = sessionsStr.match(/(\d+)\s*sessions?/);
                          if (sessionsMatch) {
                            classesText = `${sessionsMatch[1]} sessions/week`;
                          } else {
                            classesText = sessionsStr;
                          }
                        }
                      }
                    }

                    return (
                      <div
                        key={program.id}
                        onClick={() => {
                          if (isConsultantProgram) {
                            const trainerId = viewingProfile.id || viewingProfile._id || viewingProfile.trainerId;
                            navigate(`/trainer/${trainerId}`);
                          } else {
                            handleProgramSelect(program);
                          }
                        }}
                        className={`border-2 ${isPWA ? 'rounded-xl sm:rounded-2xl p-5 sm:p-6' : 'rounded-lg p-5'} cursor-pointer transition-all relative overflow-hidden group ${selectedPrograms.some(p => p.id === program.id)
                          ? isPWA ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-blue-50 shadow-xl ring-2 ring-teal-300/50' : 'border-primary bg-primary/5 shadow-lg'
                          : isPWA ? 'border-teal-200/60 bg-white hover:border-teal-400 hover:shadow-xl hover:bg-gradient-to-br hover:from-teal-50/50 hover:to-blue-50/30' : 'border-gray-200 hover:border-primary hover:shadow-md'
                          }`}
                      >
                        {isPWA && (
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        )}
                        <div className={`flex items-start justify-between mb-3 ${isPWA ? 'relative z-10' : ''}`}>
                          <h3 className={`font-semibold ${isPWA ? 'text-teal-900 text-xl sm:text-2xl' : 'text-gray-900 text-lg'}`}>{program.name}</h3>
                          {program.isActive && (
                            <span className={`inline-flex items-center ${isPWA ? 'px-3 py-1.5 rounded-xl bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200/50 text-xs font-semibold shadow-sm' : 'px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium'}`}>
                              <FaCheckCircle className="mr-1" />
                              Active
                            </span>
                          )}
                        </div>

                        {program.description && (
                          <p className={`${isPWA ? 'text-base text-teal-700 mb-3 line-clamp-2 relative z-10' : 'text-sm text-gray-600 mb-3 line-clamp-2'}`}>
                            {program.description}
                          </p>
                        )}

                        {/* Duration and Classes Info */}
                        <div className={`flex flex-wrap gap-2 sm:gap-3 mb-3 ${isPWA ? 'text-sm text-teal-600 relative z-10' : 'text-xs text-gray-500'}`}>
                          {isConsultantProgram ? (
                            <>
                              {timeWindowText && (
                                <div className={`flex items-center ${isPWA ? 'bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-100/50' : ''}`}>
                                  <FaClock className={`mr-1.5 ${isPWA ? 'text-teal-500' : ''}`} />
                                  <span className={isPWA ? 'font-medium' : ''}>{timeWindowText}</span>
                                </div>
                              )}
                              {slotDurationsText && (
                                <div className={`flex items-center ${isPWA ? 'bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100/50' : ''}`}>
                                  <FaBook className={`mr-1.5 ${isPWA ? 'text-blue-500' : ''}`} />
                                  <span className={isPWA ? 'font-medium' : ''}>{slotDurationsText}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {durationText && (
                                <div className={`flex items-center ${isPWA ? 'bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-100/50' : ''}`}>
                                  <FaClock className={`mr-1.5 ${isPWA ? 'text-teal-500' : ''}`} />
                                  <span className={isPWA ? 'font-medium' : ''}>{durationText}</span>
                                </div>
                              )}
                              {classesText && (
                                <div className={`flex items-center ${isPWA ? 'bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100/50' : ''}`}>
                                  <FaBook className={`mr-1.5 ${isPWA ? 'text-blue-500' : ''}`} />
                                  <span className={isPWA ? 'font-medium' : ''}>{classesText}</span>
                                </div>
                              )}
                            </>
                          )}
                          {program.enrollmentCount !== undefined && program.enrollmentCount > 0 && (
                            <div className={`flex items-center ${isPWA ? 'text-green-700 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200/50 px-3 py-1.5 rounded-lg font-semibold shadow-sm' : 'text-green-600 bg-green-50 px-2 py-1 rounded-full'}`}>
                              <FaUserTie className="mr-1.5" size={isPWA ? 14 : 10} />
                              <span className={isPWA ? 'text-sm' : 'font-medium'}>{program.enrollmentCount} {program.enrollmentCount === 1 ? 'member' : 'members'} enrolled</span>
                            </div>
                          )}
                        </div>

                        {/* Pricing Information */}
                        <div className={`${isPWA ? 'border-t-2 border-teal-200/60 pt-4 mt-4 relative z-10' : 'border-t border-gray-200 pt-3 mt-3'}`}>
                          {isConsultantProgram && program.slotPrices && Object.keys(program.slotPrices).length > 0 ? (
                            <div className="space-y-2">
                              {(() => {
                                const slotPricesArray = Object.entries(program.slotPrices);
                                const prices = slotPricesArray.map(([_, price]) => Number(price) || 0);
                                const minPrice = Math.min(...prices.filter(p => p > 0));

                                return (
                                  <>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-baseline gap-2">
                                        <span className={`${isPWA ? 'text-lg text-teal-600 font-medium' : 'text-sm text-gray-500'}`}>Starting from</span>
                                        <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                          {formatCurrency(minPrice)}
                                        </span>
                                      </div>
                                    </div>
                                    {slotPricesArray.length > 1 && (
                                      <div className="mt-2">
                                        <p className={`${isPWA ? 'text-xs text-teal-600 mb-1' : 'text-xs text-gray-500 mb-1'}`}>All durations:</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {slotPricesArray.map(([duration, price]) => (
                                            <span key={duration} className={`${isPWA ? 'text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded border border-teal-100/50' : 'text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded'}`}>
                                              {duration}: {formatCurrency(price)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (program.price > 0 || program.currentPrice > 0 || program.basePrice > 0 || program.discountInfo?.discountedPrice > 0 || program.discountInfo?.originalPrice > 0) ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-baseline gap-2">
                                  {program.installmentInfo?.hasInstallmentPlan ? (
                                    <>
                                      <div className="flex flex-col">
                                        <div className="flex items-baseline gap-2">
                                          <span className={`${isPWA ? 'text-base text-teal-400' : 'text-sm text-gray-400'} line-through`}>
                                            {formatCurrency(program.price || program.currentPrice || program.basePrice || 0)}
                                          </span>
                                          <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                            {formatCurrency((program.price || program.currentPrice || program.basePrice || 0) / 2)}
                                          </span>
                                        </div>
                                      </div>
                                    </>
                                  ) : program.discountInfo?.hasDiscount ? (
                                    <>
                                      <span className={`${isPWA ? 'text-base text-teal-400' : 'text-sm text-gray-400'} line-through`}>
                                        {formatCurrency(program.discountInfo.originalPrice)}
                                      </span>
                                      <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                        {formatCurrency(program.discountInfo.discountedPrice)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-700' : 'text-lg font-bold text-primary'}`}>
                                      {formatCurrency(program.price || program.currentPrice || program.basePrice || program.discountInfo?.discountedPrice || program.discountInfo?.originalPrice || 0)}
                                    </span>
                                  )}
                                </div>
                                {(program.discountInfo?.hasDiscount || program.hasDiscount) && (program.discountInfo?.discountPercentage || program.discountPercentage) > 0 && (
                                  <span className={`inline-flex items-center ${isPWA ? 'px-3 py-1.5 rounded-xl bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200/50 text-xs font-semibold shadow-sm' : 'px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium'}`}>
                                    <FaTag className="mr-1.5" />
                                    {(program.discountInfo?.discountPercentage || program.discountPercentage)}% OFF
                                  </span>
                                )}
                              </div>
                              {program.installmentInfo?.hasInstallmentPlan && (
                                <div className={`flex flex-col gap-1 ${isPWA ? 'mt-2' : 'mt-1'}`}>
                                  <div className={`flex items-center ${isPWA ? 'text-sm text-blue-700 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200/50 px-3 py-1.5 rounded-lg w-fit font-semibold shadow-sm' : 'text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded w-fit'}`}>
                                    <FaCreditCard className="mr-1.5" size={isPWA ? 14 : 10} />
                                    <span>Pay in installments available</span>
                                  </div>
                                  <div className={`flex items-center ${isPWA ? 'text-xs text-emerald-700 bg-emerald-50 border border-emerald-200/50 px-2 py-1 rounded-lg w-fit font-bold animate-pulse' : 'text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded w-fit font-bold'}`}>
                                    <FaTag className="mr-1" size={isPWA ? 12 : 8} />
                                    <span>SAVE UPTO 50% WITH ANNUAL PLAN</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 italic">
                              Pricing not available
                            </div>
                          )}
                        </div>

                        {/* Select/Deselect Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isConsultantProgram) {
                              const trainerId = viewingProfile.id || viewingProfile._id || viewingProfile.trainerId;
                              const trainerIdStr = String(trainerId).replace(/[^a-zA-Z0-9-]/g, '');
                              const trainerRoute = isStudentRoute ? `/student/trainer/${trainerIdStr}` : `/trainer/${trainerIdStr}`;
                              navigate(trainerRoute);
                            } else {
                              handleProgramSelect(program);
                            }
                          }}
                          className={`w-full mt-3 ${isPWA ? 'py-3 px-4 rounded-xl' : 'py-2 px-4 rounded-lg'} font-medium transition-all duration-300 relative z-10 ${selectedPrograms.some(p => p.id === program.id)
                            ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600 shadow-lg' : 'bg-primary text-white hover:bg-primary/90'
                            : isPWA ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-teal-700 hover:from-gray-200 hover:to-gray-300 border border-teal-200/50' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isConsultantProgram
                            ? 'View Profile to Book'
                            : (selectedPrograms.some(p => p.id === program.id) ? '✓ Selected (Click to Deselect)' : 'Select Program')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No Programs Message */}
            {(!viewingProfile.programs || viewingProfile.programs.length === 0) && (
              <div className="text-center py-6">
                <p className={`${isPWA ? 'text-teal-700 text-lg mb-4' : 'text-gray-500 italic mb-4'}`}>No programs available for this instructor yet</p>
                <button
                  onClick={() => {
                    const trainerId = viewingProfile.id || viewingProfile._id || viewingProfile.trainerId;
                    if (trainerId) {
                      let trainerIdStr = '';
                      if (typeof trainerId === 'object' && trainerId.toString) {
                        trainerIdStr = trainerId.toString();
                      } else {
                        trainerIdStr = String(trainerId);
                      }
                      setShowNotificationModal(true);
                      sessionStorage.setItem('notificationTrainerId', trainerIdStr);
                      sessionStorage.setItem('notificationTrainerName', viewingProfile.name || 'Trainer');
                    }
                  }}
                  className={`px-6 py-3 ${isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600' : 'bg-primary hover:bg-primary/90'} text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 mx-auto shadow-lg`}
                >
                  <FaBell />
                  Notify Me When Available
                </button>
              </div>
            )}
          </div>
        </div>

        {/* CTA Buttons */}
        {selectedPrograms.length > 0 && (
          <div className={`mb-4 ${isPWA ? 'p-5 bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl border-2 border-teal-200/60 shadow-md' : 'p-4 bg-primary/10 rounded-lg border border-primary/20'} relative z-10`}>
            <p className={`${isPWA ? 'text-base text-teal-800 font-semibold mb-2' : 'text-sm text-gray-700 mb-2'}`}>
              <strong>Selected Program{selectedPrograms.length > 1 ? 's' : ''}:</strong>
            </p>
            <ul className={`list-disc list-inside ${isPWA ? 'text-base text-teal-700' : 'text-sm text-gray-600'}`}>
              {selectedPrograms.map((program) => (
                <li key={program.id}>{program.name}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={`flex flex-col sm:flex-row gap-4 pt-6 ${isPWA ? 'border-t-2 border-teal-200/60' : 'border-t border-gray-200'} relative z-10`}>
          <button
            onClick={() => {
              setViewingProfile(null);
              setSelectedPrograms([]);
            }}
            className={`flex-1 ${isPWA ? 'bg-gradient-to-r from-gray-200 to-gray-300 text-teal-800 py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-md hover:shadow-lg hover:from-gray-300 hover:to-gray-400' : 'bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300'} transition-all duration-300 flex items-center justify-center`}
          >
            <FaArrowLeft className="mr-2" />
            Back to Instructors
          </button>
          {isAuthenticated ? (
            <button
              onClick={handleSubmitPlanRequest}
              disabled={selectedPrograms.length === 0}
              className={`flex-1 ${isPWA ? 'py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl' : 'py-3 px-6 rounded-lg font-medium'} transition-all duration-300 flex items-center justify-center ${selectedPrograms.length > 0
                ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600' : 'bg-primary text-white hover:bg-primary/90'
                : isPWA ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              <FaBook className="mr-2" />
              {selectedPrograms.length > 0
                ? `Enroll in ${selectedPrograms.length} Program${selectedPrograms.length > 1 ? 's' : ''}`
                : 'Select a Program to Enroll'}
            </button>
          ) : (
            <>
              <button
                onClick={handleSubmitPlanRequest}
                disabled={selectedPrograms.length === 0}
                className={`flex-1 ${isPWA ? 'py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl' : 'py-3 px-6 rounded-lg font-medium'} transition-all duration-300 flex items-center justify-center ${selectedPrograms.length > 0
                  ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600' : 'bg-primary text-white hover:bg-primary/90'
                  : isPWA ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                <FaUserPlus className="mr-2" />
                {selectedPrograms.length > 0
                  ? `Sign Up to Enroll (${selectedPrograms.length})`
                  : 'Select a Program First'}
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  const trainerId = viewingProfile.id || viewingProfile._id || viewingProfile.trainerId;
                  if (trainerId) {
                    let trainerIdStr = '';
                    if (typeof trainerId === 'object' && trainerId.toString) {
                      trainerIdStr = trainerId.toString();
                    } else {
                      trainerIdStr = String(trainerId);
                    }
                    params.set('trainerId', trainerIdStr);
                    // Include selected program IDs if any are selected
                    selectedPrograms.forEach((program) => {
                      params.append('programId', program.id);
                    });
                  }
                  navigate(`/login?${params.toString()}`);
                }}
                className={`flex-1 ${isPWA ? 'bg-gradient-to-r from-gray-200 to-gray-300 text-teal-800 py-3.5 px-6 rounded-xl sm:rounded-2xl font-bold shadow-md hover:shadow-lg hover:from-gray-300 hover:to-gray-400' : 'bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300'} transition-all duration-300 flex items-center justify-center`}
              >
                <FaSignInAlt className="mr-2" />
                Sign In
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Show service category selection when no serviceName and no selectedService
  if (!serviceName && !actualServiceName && !selectedService) {
    // Show loading state
    if (isLoading && availableServices.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="animate-spin text-6xl mx-auto text-primary mb-4" />
            <p className="text-gray-600">Loading available programs...</p>
          </div>
        </div>
      );
    }

    // Show error state
    if (apiError && !isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <FaExclamationTriangle className="text-6xl mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Programs</h3>
            <p className="text-gray-500 mb-6">{apiError.message || 'Failed to load programs'}</p>
            <button
              onClick={fetchPrograms}
              disabled={isLoading}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSpinner className={isLoading ? 'animate-spin' : ''} />
              Retry
            </button>
          </div>
        </div>
      );
    }

    // Show empty state
    if (!isLoading && availableServices.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
          <div className="text-center">
            <FaBookOpen className="text-6xl mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Programs Available</h3>
            <p className="text-gray-500 mb-6">There are currently no training programs available. Please check back later.</p>
          </div>
        </div>
      );
    }

    // Show service categories if available
    if (availableServices.length > 0 && !isLoading) {
      return (
        <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gray-50'}`}>
          <div className={`relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-gradient-to-r from-blue-600 to-purple-700'} ${isPWA ? 'text-gray-900' : 'text-white'}`}>
            {isPWA && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
            )}
            {!isPWA && (
              <div className="absolute inset-0 bg-black opacity-20"></div>
            )}
            <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-teal-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'}`}>
                    Browse Training Programs
                  </h1>
                  <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-xl opacity-90'}`}>
                    Discover amazing fitness programs and expert trainers
                  </p>
                </div>
                {!isPWA && (
                  <div className="hidden md:block">
                    <img src="/src/images/yg1.jpg" alt="Yoga" className="w-32 h-32 rounded-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-6 sm:py-8' : 'py-8'}`}>
            {/* Enhanced Section Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                </div>
                <h3 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} font-bold ${isPWA ? 'text-teal-800' : 'text-gray-900'}`}>
                  Select a Program Category
                </h3>
                <div className="relative">
                  <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                </div>
              </div>
              <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-teal-600/70' : 'text-gray-600'}`}>
                Choose from our wide range of certified training programs
              </p>
            </div>

            {/* Enhanced Service Cards Grid */}
            <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-xl' : 'bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/20'} rounded-xl sm:rounded-2xl overflow-hidden`}>
              <div className={`${isPWA ? 'p-4 sm:p-6' : 'p-4'} max-h-[600px] overflow-y-auto overflow-x-hidden`}>
                {(() => {
                  const getServiceIcon = (serviceType) => {
                    const iconMap = {
                      'yoga-trainer': FaSpinner,
                      'dietitian': FaUtensils,
                      'gym-trainer': FaDumbbell,
                      'zumba-trainer': FaRunning,
                      'reiki-healer': FaMagic,
                      'astrologer': FaStar,
                      'numerologer': FaSeedling,
                      'ayurveda-practitioner': FaHeartbeat,
                      'home-tuition': FaBook,
                      'tarot-card-reader': FaStar
                    };
                    return iconMap[serviceType] || FaSpinner;
                  };

                  const getServiceName = (categoryName) => {
                    let cleanName = categoryName.replace(/\s*Programs?$/i, '').trim();
                    const nameMap = {
                      'Yoga Trainer': 'Yoga Trainer', 'Yoga Trainer Programs': 'Yoga Trainer', 'Yoga': 'Yoga Trainer',
                      'Dietitian': 'Dietitian', 'Dietitian Programs': 'Dietitian',
                      'Gym Trainer': 'Gym Trainer', 'Gym Trainer Programs': 'Gym Trainer', 'Gym Instructor': 'Gym Trainer', 'Gym Instructor Programs': 'Gym Trainer', 'Gym': 'Gym Trainer',
                      'Zumba Trainer': 'Zumba Trainer', 'Zumba Trainer Programs': 'Zumba Trainer', 'Zumba Instructor': 'Zumba Trainer', 'Zumba Instructor Programs': 'Zumba Trainer', 'Zumba': 'Zumba Trainer',
                      'Reiki Healer': 'Reiki Healer', 'Reiki Healer Programs': 'Reiki Healer', 'Reiki': 'Reiki Healer',
                      'Astrologer': 'Astrologer', 'Astrologer Programs': 'Astrologer', 'Astrology': 'Astrologer', 'Astrology Programs': 'Astrologer',
                      'Numerologer': 'Numerologer', 'Numerologer Programs': 'Numerologer', 'Numerology': 'Numerologer', 'Numerology Programs': 'Numerologer',
                      'Ayurveda Practitioner': 'Ayurvedacharya', 'Ayurveda Practitioner Programs': 'Ayurvedacharya', 'Ayurvedacharya': 'Ayurvedacharya', 'Ayurvedacharya Programs': 'Ayurvedacharya', 'Ayurveda': 'Ayurvedacharya',
                      'Home Tuition': 'Home Tuition', 'Home Tuition Programs': 'Home Tuition',
                      'Home Tutor': 'Home Tutor', 'Home Tutor Programs': 'Home Tutor',
                      'Tarot Card Reader': 'Tarot Card Reader', 'Tarot Card Reader Programs': 'Tarot Card Reader'
                    };
                    if (nameMap[cleanName]) return nameMap[cleanName];
                    if (nameMap[categoryName]) return nameMap[categoryName];
                    const lowerCleanName = cleanName.toLowerCase();
                    const lowerCategoryName = categoryName.toLowerCase();
                    for (const [key, value] of Object.entries(nameMap)) {
                      if (key.toLowerCase() === lowerCleanName || key.toLowerCase() === lowerCategoryName) return value;
                    }
                    return cleanName;
                  };

                  const getPWAIconColor = (index) => {
                    const colors = [
                      { bg: 'from-teal-100 to-blue-100', icon: 'text-teal-600', border: 'border-teal-200/60', hover: 'hover:from-teal-50 hover:to-blue-50' },
                      { bg: 'from-blue-100 to-indigo-100', icon: 'text-blue-600', border: 'border-blue-200/60', hover: 'hover:from-blue-50 hover:to-indigo-50' },
                      { bg: 'from-indigo-100 to-purple-100', icon: 'text-indigo-600', border: 'border-indigo-200/60', hover: 'hover:from-indigo-50 hover:to-purple-50' },
                      { bg: 'from-cyan-100 to-teal-100', icon: 'text-cyan-600', border: 'border-cyan-200/60', hover: 'hover:from-cyan-50 hover:to-teal-50' },
                      { bg: 'from-emerald-100 to-green-100', icon: 'text-emerald-600', border: 'border-emerald-200/60', hover: 'hover:from-emerald-50 hover:to-green-50' },
                      { bg: 'from-sky-100 to-blue-100', icon: 'text-sky-600', border: 'border-sky-200/60', hover: 'hover:from-sky-50 hover:to-blue-50' },
                      { bg: 'from-violet-100 to-purple-100', icon: 'text-violet-600', border: 'border-violet-200/60', hover: 'hover:from-violet-50 hover:to-purple-50' },
                      { bg: 'from-purple-100 to-pink-100', icon: 'text-purple-600', border: 'border-purple-200/60', hover: 'hover:from-purple-50 hover:to-pink-50' },
                      { bg: 'from-pink-100 to-rose-100', icon: 'text-pink-600', border: 'border-pink-200/60', hover: 'hover:from-pink-50 hover:to-rose-50' }
                    ];
                    return colors[index % colors.length];
                  };

                  const renderServiceCard = (service, index, isConsultantGroup) => {
                    const ServiceIcon = getServiceIcon(service.type);
                    let serviceName = getServiceName(service.name);

                    if (!isConsultantGroup) {
                      if (serviceName === 'Reiki Healer') serviceName = 'Reiki Trainer';
                      if (serviceName === 'Tarot Card Reader') serviceName = 'Tarot Card Trainer';
                    }

                    const colors = getPWAIconColor(index);

                    return (
                      <div
                        key={service.id || service.type || `service-${index}`}
                        className={`group bg-gradient-to-br ${colors.bg} ${colors.hover} border-2 ${colors.border} ${isPWA ? 'p-4 sm:p-5' : 'p-4'} rounded-xl sm:rounded-2xl transition-all duration-300 cursor-pointer shadow-md hover:shadow-xl hover:scale-105 active:scale-95 relative overflow-hidden`}
                        onClick={() => handleServiceSelect(service)}
                      >
                        <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${colors.bg} rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                        <div className="relative z-10 flex items-center gap-4">
                          <div className={`bg-gradient-to-br ${colors.bg} p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg ring-2 ring-white/50 group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                            <ServiceIcon className={`${isPWA ? 'h-6 w-6 sm:h-7 sm:w-7' : 'h-6 w-6'} ${colors.icon}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`${isPWA ? 'text-base sm:text-lg' : 'text-base'} font-bold ${isPWA ? 'text-gray-800' : 'text-gray-900'} group-hover:text-teal-700 transition-colors`}>
                              {serviceName}
                            </h4>
                            <div className="text-xs text-gray-500 mt-0.5">
                              <p>
                                {(() => {
                                  let count = 0;
                                  if (service.trainers && Array.isArray(service.trainers)) {
                                    count = service.trainers.length;
                                  } else if (service.trainerCount !== undefined && service.trainerCount !== null) {
                                    count = Number(service.trainerCount);
                                  }
                                  const catTitleSingle = isConsultantGroup ? 'consultant' : 'instructor';
                                  const catTitlePlural = isConsultantGroup ? 'consultants' : 'instructors';
                                  return `${count} ${count === 1 ? catTitleSingle : catTitlePlural} available`;
                                })()}
                              </p>
                              {(() => {
                                const aiSupportedTypes = ['dietitian', 'astrologer', 'ayurvedacharya', 'reiki-healer', 'numerologer', 'tarot-card-reader', 'home-tutor'];
                                const typeForAi = service.type || (service.name ? service.name.toLowerCase().replace(/\s*programs?$/i, '').replace(/\s+/g, '-') : '');
                                const hasAi = isAiAgentsEnabled && aiSupportedTypes.includes(typeForAi);
                                if (isConsultantGroup && hasAi) {
                                  return (
                                    <p className="text-purple-600 font-medium">
                                      1 AI consultant available
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                          <div className={`${colors.icon} opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:translate-x-1`}>
                            <FaChevronRight className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    );
                  };

                  const isConsultantCategory = (s) => (s.name || '').toLowerCase().match(/dietitian|astrologer|astrology|numerologer|numerology|tarot|ayurvedacharya|ayurveda|reiki|consultation|home tutor/i) || s.type === 'tarot-card-reader' || s.type === 'astrologer' || s.type === 'numerologer' || s.type === 'ayurveda-practitioner' || s.type === 'home-tutor';

                  const isInstructorCategory = (s) => {
                    const name = (s.name || '').toLowerCase();
                    const type = s.type || '';
                    if (name.match(/reiki|tarot/i) || type.match(/reiki-healer|tarot-card-reader/i)) return true;
                    return !isConsultantCategory(s);
                  };

                  const validServices = availableServices.filter(s => s.type !== 'others' && s.name !== 'Others' && s.name !== 'Others Programs' && s.name !== 'Other');

                  // Deduplicate based on standardized names
                  const uniqueServices = [];
                  const seenNames = new Set();
                  validServices.forEach(s => {
                    const stdName = getServiceName(s.name);
                    if (!seenNames.has(stdName)) {
                      seenNames.add(stdName);
                      uniqueServices.push(s);
                    }
                  });

                  const instructors = uniqueServices.filter(s => isInstructorCategory(s));
                  const consultants = uniqueServices.filter(s => isConsultantCategory(s));

                  return (
                    <div className="space-y-8">
                      {instructors.length > 0 && (
                        <div>
                          <div
                            className={`flex items-center justify-between cursor-pointer ${isPWA ? 'bg-teal-50/50 p-3 rounded-xl border border-teal-100 mb-4' : 'mb-4 border-b pb-2'}`}
                            onClick={() => isPWA && setIsInstructorsExpanded(!isInstructorsExpanded)}
                          >
                            <h4 className={`flex items-center gap-2 ${isPWA ? 'text-xl font-bold text-teal-800 m-0' : 'text-xl font-bold text-gray-800 m-0'}`}>
                              <div className="w-1.5 h-6 bg-gradient-to-b from-teal-500 to-blue-500 rounded-full"></div>
                              Instructors
                            </h4>
                            {isPWA && (
                              <FaChevronDown className={`text-teal-600 transition-transform ${isInstructorsExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          {(!isPWA || isInstructorsExpanded) && (
                            <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-3 sm:gap-4`}>
                              {instructors.map((service, index) => renderServiceCard(service, index, false))}
                            </div>
                          )}
                        </div>
                      )}

                      {consultants.length > 0 && (
                        <div>
                          <div
                            className={`flex items-center justify-between cursor-pointer ${isPWA ? 'bg-purple-50/50 p-3 rounded-xl border border-purple-100 mb-4' : 'mb-4 border-b pb-2'}`}
                            onClick={() => isPWA && setIsConsultantsExpanded(!isConsultantsExpanded)}
                          >
                            <h4 className={`flex items-center gap-2 ${isPWA ? 'text-xl font-bold text-purple-800 m-0' : 'text-xl font-bold text-gray-800 m-0'}`}>
                              <div className="w-1.5 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                              Consultants
                            </h4>
                            {isPWA && (
                              <FaChevronDown className={`text-purple-600 transition-transform ${isConsultantsExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          {(!isPWA || isConsultantsExpanded) && (
                            <div className={`grid ${isPWA ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-3 sm:gap-4`}>
                              {consultants.map((service, index) => renderServiceCard(service, index, true))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Determine service name to display
  const serviceNameToDisplay = selectedService
    ? (selectedService.name || '').replace(' Programs', ' Services')
    : (actualServiceName ? `${actualServiceName} Services` : 'Services');

  // Determine professional title based on service type
  const isConsultantService = (serviceNameToDisplay || actualServiceName || '').toLowerCase().match(/dietitian|astrologer|astrology|numerologer|numerology|tarot|ayurvedacharya|ayurveda|reiki|consultation|home tutor/i);
  const titleSingle = isConsultantService ? 'consultant' : 'instructor';
  const titlePlural = isConsultantService ? 'consultants' : 'instructors';
  const titleSingleCap = isConsultantService ? 'Consultant' : 'Instructor';
  const titlePluralCap = isConsultantService ? 'Consultants' : 'Instructors';

  return (
    <>
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className={`mb-8 ${isPWA ? 'relative' : ''}`}>
            {isPWA && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400 rounded-full"></div>
            )}
            <button
              onClick={() => {
                if (selectedService) {
                  setSelectedService(null);
                } else {
                  navigate(-1);
                }
              }}
              className={`flex items-center ${isPWA ? 'text-teal-700 hover:text-teal-800 font-semibold mb-4 px-4 py-2 rounded-lg hover:bg-teal-50 transition-all' : 'text-gray-600 hover:text-primary mb-4 transition-colors'}`}
            >
              <FaArrowLeft className={`mr-2 ${isPWA ? 'text-teal-600' : ''}`} />
              Back
            </button>
            <div className={`${isPWA ? 'flex items-center gap-3 mb-3' : ''}`}>
              {isPWA && (
                <div className="relative">
                  <div className="absolute left-0 w-1 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                </div>
              )}
              <h1 className={`${isPWA ? 'text-3xl sm:text-4xl font-bold text-teal-900 mb-2' : 'text-4xl font-bold text-gray-900 mb-2'}`}>
                {serviceNameToDisplay || actualServiceName || 'Services'}
              </h1>
              {isPWA && (
                <div className="relative">
                  <div className="absolute left-0 w-1 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full"></div>
                </div>
              )}
            </div>
            <p className={`${isPWA ? 'text-base sm:text-lg text-teal-700 font-medium' : 'text-gray-600'}`}>
              Discover certified {(serviceNameToDisplay || actualServiceName || 'service').toLowerCase().replace(' services', '')} {titlePlural}
            </p>
          </div>

          {/* Search and Sort Bar */}
          <div className={`mb-6 space-y-4 ${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-lg' : ''}`}>
            {/* Search Bar */}
            <div className="relative">
              <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder={`Search ${titlePlural} by name, expertise, or certification...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 ${isPWA ? 'border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50 rounded-xl' : 'border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent'}`}
              />
            </div>

            {/* Sort Controls */}
            <div className="flex flex-wrap items-center gap-4">
              <span className={`${isPWA ? 'text-teal-800 font-semibold text-base' : 'text-gray-700 font-medium'}`}>Sort by:</span>
              <button
                onClick={() => handleSort('rating')}
                className={`flex items-center gap-2 px-4 py-2 ${isPWA ? 'rounded-xl border-2 transition-all duration-300' : 'rounded-lg border transition-colors'} ${sortBy === 'rating'
                  ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white border-teal-400 shadow-lg' : 'bg-primary text-white border-primary'
                  : isPWA ? 'bg-white text-teal-700 border-teal-200/60 hover:border-teal-400 hover:bg-teal-50' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                  }`}
              >
                Rating {getSortIcon('rating')}
              </button>
              <button
                onClick={() => handleSort('name')}
                className={`flex items-center gap-2 px-4 py-2 ${isPWA ? 'rounded-xl border-2 transition-all duration-300' : 'rounded-lg border transition-colors'} ${sortBy === 'name'
                  ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white border-teal-400 shadow-lg' : 'bg-primary text-white border-primary'
                  : isPWA ? 'bg-white text-teal-700 border-teal-200/60 hover:border-teal-400 hover:bg-teal-50' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                  }`}
              >
                Name {getSortIcon('name')}
              </button>
              <button
                onClick={() => handleSort('experience')}
                className={`flex items-center gap-2 px-4 py-2 ${isPWA ? 'rounded-xl border-2 transition-all duration-300' : 'rounded-lg border transition-colors'} ${sortBy === 'experience'
                  ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white border-teal-400 shadow-lg' : 'bg-primary text-white border-primary'
                  : isPWA ? 'bg-white text-teal-700 border-teal-200/60 hover:border-teal-400 hover:bg-teal-50' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                  }`}
              >
                Experience {getSortIcon('experience')}
              </button>
              <button
                onClick={() => handleSort('certification')}
                className={`flex items-center gap-2 px-4 py-2 ${isPWA ? 'rounded-xl border-2 transition-all duration-300' : 'rounded-lg border transition-colors'} ${sortBy === 'certification'
                  ? isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 text-white border-teal-400 shadow-lg' : 'bg-primary text-white border-primary'
                  : isPWA ? 'bg-white text-teal-700 border-teal-200/60 hover:border-teal-400 hover:bg-teal-50' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                  }`}
              >
                Certification {getSortIcon('certification')}
              </button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-4xl text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Trainers Grid */}
          {!isLoading && !error && (
            <>
              {filteredAndSortedTrainers.length > 0 && (
                <>
                  <div className={`mb-4 flex justify-between items-center ${isPWA ? 'px-2' : ''}`}>
                    <div className={`${isPWA ? 'text-teal-700 font-semibold text-base' : 'text-gray-600'}`}>
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedTrainers.length)} of {filteredAndSortedTrainers.length} {filteredAndSortedTrainers.every(t => t.isAi) ? 'AI ' : ''}{filteredAndSortedTrainers.length === 1 ? titleSingle : titlePlural}
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 ${isPWA ? 'sm:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4 sm:gap-6`}>
                    {paginatedTrainers.map((trainer) => (
                      <div
                        key={trainer.id}
                        className={`${trainer.isAi 
                          ? (isPWA ? 'bg-gradient-to-br from-white to-purple-50/30 border-2 border-purple-200/60 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-5 sm:p-6 relative overflow-hidden group' : 'bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border-2 border-purple-100')
                          : (isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-5 sm:p-6 relative overflow-hidden group' : 'bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border border-gray-200')
                        } flex flex-col`}
                      >
                        {isPWA && (
                          <>
                            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${trainer.isAi ? 'from-purple-100/30 to-pink-100/20' : 'from-teal-100/30 to-blue-100/20'} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${trainer.isAi ? 'from-purple-400 via-pink-400 to-indigo-400' : 'from-teal-400 via-blue-400 to-indigo-400'} opacity-0 group-hover:opacity-100 transition-opacity duration-300 street`}></div>
                          </>
                        )}
                        {/* Trainer Image */}
                        <div className={`flex items-center mb-4 ${isPWA ? 'relative z-10' : ''}`}>
                          <div className={`mr-4 ${isPWA ? 'relative' : ''}`}>
                            {isPWA && (
                              <div className={`absolute -inset-1 bg-gradient-to-br ${trainer.isAi ? 'from-purple-200/50 to-pink-200/50' : 'from-teal-200/50 to-blue-200/50'} rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                            )}
                            <Avatar
                              src={trainer.profileImage}
                              name={trainer.name}
                              size={isPWA ? 90 : 80}
                              className={trainer.isAi 
                                ? (isPWA ? 'border-3 border-purple-400 ring-4 ring-purple-100/30 shadow-xl' : 'border-2 border-purple-400 shadow-lg p-0.5 bg-gradient-to-br from-purple-500 to-pink-500')
                                : (isPWA ? 'border-3 border-teal-300 ring-2 ring-teal-100/50 shadow-lg' : 'border-2 border-primary')
                              }
                              alt={formatName(trainer.name)}
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className={`${isPWA ? 'text-xl sm:text-2xl font-bold text-teal-900 mb-1' : 'text-xl font-bold text-gray-900 mb-1'}`}>
                              {formatName(trainer.name)}
                            </h3>
                            <div className={`flex items-center ${isPWA ? 'text-yellow-500' : 'text-yellow-500'}`}>
                              <StarRating
                                rating={trainer.rating || 0}
                                size="sm"
                                showNumber={true}
                                showReviews={!trainer.isAi}
                                reviews={trainer.reviews || 0}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Service Type */}
                        <div className={`mb-3 flex items-center gap-2 flex-wrap ${isPWA ? 'relative z-10' : ''}`}>
                          <span className={`inline-block ${trainer.isAi 
                            ? (isPWA ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200/50 px-3 py-1.5 rounded-xl text-sm font-semibold shadow-sm' : 'bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium border border-purple-200')
                            : (isPWA ? 'bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 border border-teal-200/50 px-3 py-1.5 rounded-xl text-sm font-semibold shadow-sm' : 'bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium')
                          }`}>
                            {trainer.service}
                          </span>
                          {trainer.hasReferralSystem && (
                            <span className={`inline-flex items-center gap-1 ${isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200/50 px-3 py-1.5 rounded-xl text-xs font-semibold shadow-sm' : 'bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium'}`}>
                              <FaTag className={isPWA ? 'text-green-600' : 'text-green-600'} />
                              Referral Code Available
                            </span>
                          )}
                        </div>

                        {/* Bio - Scrollable container for better UX */}
                        <div className={`${isPWA ? 'text-teal-700 text-sm sm:text-base mb-4 min-h-[4.5rem] max-h-[6.5rem] relative z-10' : 'text-gray-600 text-sm mb-4 min-h-[4.5rem] max-h-[6.5rem]'} overflow-y-auto ${isPWA ? 'scrollbar-thin scrollbar-thumb-teal-400 scrollbar-track-teal-100/30' : 'scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'} pr-2 border-r-2 ${isPWA ? 'border-teal-200/30' : 'border-gray-200'} rounded-r`}>
                          <p className="leading-relaxed pr-1">{trainer.bio || '\u00A0'}</p>
                        </div>

                        {/* Experience & Certifications (Human) or Badges (AI) */}
                        <div className={`space-y-2 mb-4 ${isPWA ? 'relative z-10' : ''}`}>
                          {trainer.isAi ? (
                            <>
                              <div className={`flex items-center ${isPWA ? 'text-purple-700 text-sm sm:text-base' : 'text-purple-600 text-sm'} font-bold`}>
                                <FaTag className={`mr-2 ${isPWA ? 'text-purple-500' : 'text-purple-500'}`} />
                                <span>Free* <span className="text-[10px] font-normal opacity-70 ml-1">(Shared daily limit, subject to change)</span></span>
                              </div>
                              <div className={`flex items-start gap-2 p-2 rounded-lg ${isPWA ? 'bg-amber-50 border border-amber-100' : 'bg-yellow-50 border border-yellow-100'}`}>
                                <FaExclamationTriangle className="text-amber-500 mt-1 flex-shrink-0 text-xs" />
                                <span className={`text-[11px] leading-tight ${isPWA ? 'text-amber-800' : 'text-amber-700'} font-medium`}>
                                  CAUTION: AI-generated advice. Not a substitute for professional medical or expert diagnosis.
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              {trainer.yearsOfExperience > 0 && (
                                <div className={`flex items-center ${isPWA ? 'text-teal-700 text-sm sm:text-base' : 'text-gray-600 text-sm'}`}>
                                  <FaClock className={`mr-2 ${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                                  <span>{trainer.yearsOfExperience} years of experience</span>
                                </div>
                              )}
                            </>
                          )}
                          {trainer.certification && (
                            <div className={`flex items-center ${isPWA ? 'text-teal-700 text-sm sm:text-base' : 'text-gray-600 text-sm'}`}>
                              <FaCertificate className={`mr-2 ${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                              <span className="line-clamp-1">{trainer.certification}</span>
                            </div>
                          )}
                        </div>

                        {/* CTA Button - Push to bottom with mt-auto */}
                        <button
                          onClick={() => {
                            if (trainer.isAi) {
                              if (isAuthenticated || isStudentRoute) {
                                setSelectedAiService(trainer);
                                setShowAiModal(true);
                              } else {
                                navigate(`/login?redirect=/student/services/${trainer.service?.toLowerCase().replace(/\\s+/g, '-') || trainer.name.toLowerCase().replace(/\\s+/g, '-')}`);
                              }
                              return;
                            }

                            const isConsultantTrainer = trainer.isConsultant || trainer.partnerType === 'consultant' || ['astrologer', 'numerologer', 'dietitian', 'ayurvedacharya', 'reiki healer'].includes(trainer.service?.toLowerCase());

                            if (isAuthenticated && !isConsultantTrainer) {
                              // Show program selection for authenticated regular instructors
                              if (selectedService && trainer.programs && trainer.programs.length > 0) {
                                setViewingProfile(trainer);
                                setSelectedTrainer(trainer);
                              } else {
                                // Navigate to trainer profile which has the programs
                                let trainerIdStr = '';
                                const possibleId = trainer.id || trainer._id || trainer.trainerId;

                                if (possibleId) {
                                  if (typeof possibleId === 'object') {
                                    if (possibleId.toString && typeof possibleId.toString === 'function') {
                                      trainerIdStr = possibleId.toString();
                                    } else if (possibleId._id) {
                                      trainerIdStr = String(possibleId._id);
                                    } else if (possibleId.$oid) {
                                      trainerIdStr = possibleId.$oid;
                                    } else {
                                      trainerIdStr = String(possibleId);
                                    }
                                  } else {
                                    trainerIdStr = String(possibleId);
                                  }
                                }

                                if (trainerIdStr && trainerIdStr.length >= 10) {
                                  trainerIdStr = trainerIdStr.replace(/[^a-zA-Z0-9-]/g, '');
                                  const trainerRoute = isStudentRoute ? `/student/trainer/${trainerIdStr}` : `/trainer/${trainerIdStr}`;
                                  navigate(trainerRoute);
                                } else {
                                  toast.error('Unable to load trainer profile. Invalid trainer ID.');
                                }
                              }
                            } else {
                              // Navigate directly to full profile page for public users or consultants
                              let trainerIdStr = '';

                              // Try to get ID from various possible fields
                              const possibleId = trainer.id || trainer._id || trainer.trainerId;

                              if (possibleId) {
                                // Handle ObjectId or string
                                if (typeof possibleId === 'object') {
                                  // ObjectId object - try toString method
                                  if (possibleId.toString && typeof possibleId.toString === 'function') {
                                    trainerIdStr = possibleId.toString();
                                  } else if (possibleId._id) {
                                    // Nested _id
                                    trainerIdStr = String(possibleId._id);
                                  } else if (possibleId.$oid) {
                                    // MongoDB extended JSON format
                                    trainerIdStr = possibleId.$oid;
                                  } else {
                                    // Try to extract from object
                                    trainerIdStr = String(possibleId);
                                  }
                                } else {
                                  // Already a string or number
                                  trainerIdStr = String(possibleId);
                                }
                              }

                              // Validate trainerId format
                              if (!trainerIdStr || trainerIdStr.length < 10) {
                                console.error('[Services] Invalid trainer ID:', {
                                  trainerIdStr,
                                  trainer,
                                  id: trainer.id,
                                  _id: trainer._id,
                                  possibleId
                                });
                                toast.error('Unable to load trainer profile. Invalid trainer ID.');
                                return;
                              }

                              // Clean up trainerId and navigate
                              trainerIdStr = trainerIdStr.replace(/[^a-zA-Z0-9-]/g, '');
                              const trainerRoute = isStudentRoute ? `/student/trainer/${trainerIdStr}` : `/trainer/${trainerIdStr}`;
                              navigate(trainerRoute);
                            }
                          }}
                          className={`w-full ${trainer.isAi ? (isPWA ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 rounded-xl sm:rounded-2xl font-bold shadow-lg mt-auto relative z-10' : 'bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 mt-auto') : (isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600 text-white py-3 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 mt-auto relative z-10' : 'bg-primary text-white py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors mt-auto')}`}
                        >
                          {(() => {
                            if (trainer.isAi) {
                              const trainerServiceType = trainer.type || trainer.service?.toLowerCase().replace(/\s+/g, '-') || 'general';
                              const isAlreadyEnrolled = enrolledAiServices.includes(trainerServiceType);
                              return (isAuthenticated || isStudentRoute) ? (isAlreadyEnrolled ? 'Chat with AI' : 'Enroll & Chat with AI') : 'Login to Chat with AI';
                            }
                            const isConsultantTrainer = trainer.isConsultant || trainer.partnerType === 'consultant' || ['astrologer', 'numerologer', 'dietitian', 'ayurvedacharya', 'reiki healer'].includes(trainer.service?.toLowerCase());
                            if (isAuthenticated || isStudentRoute) {
                              return isConsultantTrainer ? 'View Profile to Book' : 'View Profile & Enroll';
                            }
                            return isConsultantTrainer ? 'View Full Profile to Book' : 'View Full Profile & Enroll';
                          })()}
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:bg-primary hover:text-white'
                          }`}
                      >
                        <FaChevronLeft />
                      </button>

                      {/* Page Numbers */}
                      <div className="flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                          // Show first page, last page, current page, and pages around current
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === pageNum
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          } else if (
                            pageNum === currentPage - 2 ||
                            pageNum === currentPage + 2
                          ) {
                            return (
                              <span key={pageNum} className="px-2 text-gray-400">
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:bg-primary hover:text-white'
                          }`}
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
                </>
              )}

              {filteredAndSortedTrainers.filter(t => !t.isAi).length === 0 && (
                <div className="mb-8 text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <FaBookOpen className="text-5xl mx-auto" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {searchTerm
                      ? `No ${titlePlural} found matching "${searchTerm}"`
                      : `No ${serviceNameToDisplay || 'Service'} ${titlePluralCap} Available`
                    }
                  </h2>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    {searchTerm
                      ? `Try adjusting your search terms or browse all available ${titlePlural}.`
                      : `There are currently no ${(serviceNameToDisplay || actualServiceName || 'service').toLowerCase().replace(' services', '')} ${titlePlural} available at the moment. Please check back later or explore other services.`
                    }
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setShowNotificationModal(true)}
                      className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 mx-auto"
                    >
                      <FaBell />
                      Notify Me When Available
                    </button>
                  )}
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Notification Modal for Service-Level Notifications */}
      {showNotificationModal && (
        <TrainerAvailabilityNotificationModal
          isOpen={showNotificationModal}
          onClose={() => setShowNotificationModal(false)}
          trainerId={null}
          trainerName={actualServiceName}
          programId={null}
          programName={null}
          service={actualServiceName}
        />
      )}

      {/* AI Enrollment Modal */}
      <AiEnrollmentModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        aiService={selectedAiService}
      />
      {/* Quick Profile Completion Modal */}
      {showQuickProfileModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className={`bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col ${isPWA ? 'border-2 border-teal-200' : ''}`}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white p-6 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl"></div>
               <div className="relative z-10">
                 <div className="flex items-center justify-between mb-2">
                   <h2 className="text-xl sm:text-2xl font-bold">One Final Step!</h2>
                   <button onClick={() => setShowQuickProfileModal(false)} className="text-white/80 hover:text-white transition-colors">
                     <FaTimes className="text-xl" />
                   </button>
                 </div>
                 <p className="text-white/90 text-sm small">Please provide your basic details to complete your enrollment. This helps your trainer prepare for your sessions.</p>
               </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-5">
               {/* Phone */}
               <div className="space-y-1.5">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block">Phone Number *</label>
                 <div className="relative">
                   <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                   <input 
                     type="tel" 
                     value={quickProfileData.mobile}
                     onChange={(e) => setQuickProfileData({...quickProfileData, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10)})}
                     placeholder="10-digit mobile number"
                     className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all outline-none"
                   />
                 </div>
               </div>

               {/* Gender */}
               <div className="space-y-1.5">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block">Gender *</label>
                 <div className="grid grid-cols-3 gap-3">
                   {['male', 'female', 'other'].map(g => (
                     <button
                       key={g}
                       onClick={() => setQuickProfileData({...quickProfileData, gender: g})}
                       className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${quickProfileData.gender === g 
                         ? 'bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-200' 
                         : 'bg-white border-teal-100 text-teal-700 hover:border-teal-300'}`}
                     >
                       {toTitleCase(g)}
                     </button>
                   ))}
                 </div>
               </div>

               {/* Address */}
               <div className="space-y-4 pt-2 border-t border-teal-50">
                 <label className="text-xs font-bold text-teal-800 uppercase tracking-wider block flex items-center">
                   <FaHome className="mr-2" /> Address Details *
                 </label>
                 
                 <div className="grid grid-cols-1 gap-4">
                   <div className="space-y-1">
                     <input 
                       placeholder="Address Line 1"
                       value={quickProfileData.addressLine1}
                       onChange={(e) => setQuickProfileData({...quickProfileData, addressLine1: e.target.value})}
                       className="w-full px-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                     />
                   </div>
                   
                   <div className="grid grid-cols-2 gap-4">
                     <div className="relative">
                       <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                       <input 
                         placeholder="City"
                         value={quickProfileData.city}
                         onChange={(e) => setQuickProfileData({...quickProfileData, city: e.target.value})}
                         className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                       />
                     </div>
                     <div className="relative">
                       <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-teal-500" />
                       <input 
                         placeholder="State"
                         value={quickProfileData.state}
                         onChange={(e) => setQuickProfileData({...quickProfileData, state: e.target.value})}
                         className="w-full pl-10 pr-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                       />
                     </div>
                   </div>

                   <div className="w-1/2">
                     <input 
                       placeholder="Pincode"
                       value={quickProfileData.pincode}
                       onChange={(e) => setQuickProfileData({...quickProfileData, pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6)})}
                       className="w-full px-4 py-2.5 bg-teal-50/30 border-2 border-teal-100 rounded-xl focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none"
                     />
                   </div>
                 </div>
               </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100">
               <button
                 disabled={isUpdatingProfile}
                 onClick={async () => {
                   // Validation
                   if (!quickProfileData.mobile || quickProfileData.mobile.length < 10) return toast.error('Valid phone number is required');
                   if (!quickProfileData.addressLine1 || !quickProfileData.city || !quickProfileData.state || !quickProfileData.pincode) return toast.error('Address details are required');
                   
                   try {
                     setIsUpdatingProfile(true);
                     const updateData = {
                       mobile: quickProfileData.mobile,
                       gender: quickProfileData.gender,
                       address: {
                         addressLine1: quickProfileData.addressLine1,
                         addressLine2: '',
                         city: quickProfileData.city,
                         state: quickProfileData.state,
                         pincode: quickProfileData.pincode,
                         country: 'India'
                       }
                     };
                     
                     const response = await HttpInterceptor.post('/api/students/profile/update', updateData);
                     
                     if (response.status === 200) {
                        // Update local context
                        setSession(prev => ({
                          ...prev,
                           ...updateData
                        }));
                        
                        // Refresh cache
                        mutate('/auth/session');
                        
                        setShowQuickProfileModal(false);
                        toast.success('Details updated! Completing your enrollment...');
                        
                        // Automatically trigger enrollment now that profile is done
                        handleEnroll();
                     }
                   } catch (err) {
                     toast.error('Failed to update details. Please try again.');
                   } finally {
                     setIsUpdatingProfile(false);
                   }
                 }}
                 className="w-full py-4 px-6 bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white rounded-xl font-bold shadow-xl hover:shadow-teal-200/50 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-70"
               >
                 {isUpdatingProfile ? (
                   <FaSpinner className="animate-spin text-xl" />
                 ) : (
                   <>
                     <FaSave className="text-xl" />
                     <span>Finish Profile & Enroll Now</span>
                   </>
                 )}
               </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default Services;

